/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { MemberService } from 'app/cm/services/member-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { bindable } from 'aurelia-typed-observable-plugin';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class HouseholdCaseDiagnosesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected householdCaseId: string | null = null;

    public diagnoses: CM.MemberDiagnosis[] = [];

    private diagnosisSorting: Sorting = new Sorting();
    protected diagnosisResultCount: number = 0;
    protected diagnosisPageSize: number = 25;

    protected showMemberSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;

    ////****************** Element References ***************************************////
    protected $diagnosisPager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: MemberService,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.checkPermissions();

        this.reload();
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ***********************************************////

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();

        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
    }

    public async reload(): Promise<void> {
        await this.loadDiagnoses();
    }

    protected applyDiagnosesSort(event: SortChangeEvent): void {
        if(!this.householdCaseId) { return; }
        const { property, direction } = event.detail;
        const { diagnosisSorting } = this;
        if(direction === null) {
            diagnosisSorting.remove(property);
        } else {
            diagnosisSorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.loadDiagnoses();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadDiagnoses(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.diagnosisPageSize,
            page: this.$diagnosisPager?.page || 0,
            sorting: this.diagnosisSorting
        };

        const includes: string[] = ['member', 'metadata.created.byUser', 'diagnosis'];

        try {
            this.state.beginLoad();
            const response = await this.service.loadDiagnosesByHouseholdCaseId(this.householdCaseId, options, includes);
            this.diagnoses = response.results;
            this.diagnosisResultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

}
